import { ComponentBase } from "../../core/ComponentBase.js";

export class AIProgressMonitor extends ComponentBase {
    static styles = `
        .ai-progress-monitor {
            position: fixed; /* Use fixed to center on viewport */
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 550px; /* Increased width */
            max-width: 90vw;
            z-index: 1060; /* Higher z-index for centering */
            background-color: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius-lg);
            box-shadow: var(--bs-box-shadow-lg);
            display: flex;
            flex-direction: column;
            transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
            opacity: 0;
            transform: translate(-50%, calc(-50% + 20px)); /* Start slightly lower for animation */
            pointer-events: none;
        }
        .ai-progress-monitor.visible {
            opacity: 1;
            transform: translate(-50%, -50%);
            pointer-events: auto;
        }
        .progress-monitor-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1.25rem; /* Increased padding */
            border-bottom: 1px solid var(--bs-border-color-translucent);
            background-color: var(--bs-tertiary-bg);
            cursor: grab;
        }
        .progress-monitor-title {
            font-weight: 500;
            font-size: 1.1rem; /* Slightly larger title */
            margin: 0;
        }
        .progress-monitor-controls .btn {
            padding: 0.2rem 0.5rem;
            font-size: 1.1rem;
        }
        .progress-monitor-body {
            padding: 1.25rem;
            transition: padding 0.3s, max-height 0.3s;
            max-height: 60vh; /* Allow more vertical space */
            overflow: hidden;
        }
        .ai-progress-monitor.minimized .progress-monitor-body {
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
        }
        .progress-monitor-message {
            font-size: 1rem;
            color: var(--bs-secondary-color);
            margin-bottom: 1rem;
            min-height: 1.2em;
        }
        .log-container {
            margin-top: 1rem;
            max-height: 250px; /* Increased log height */
            overflow-y: auto;
            background-color: var(--bs-tertiary-bg);
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius-sm);
            padding: 0.75rem;
            font-family: monospace;
            font-size: 0.8rem;
        }
        .log-entry {
            white-space: pre-wrap;
            word-break: break-all;
            padding-bottom: 0.3rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            margin-bottom: 0.3rem;
        }
        .log-entry:last-child {
            border-bottom: none;
            margin-bottom: 0;
        }
    `;

    constructor(config) {
        super(config);
        this.jobId = null;
        this.isMinimized = false;
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'ai-progress-monitor';
        this.container.innerHTML = this.renderStructure();

        this.titleEl = this.container.querySelector('.progress-monitor-title');
        this.messageEl = this.container.querySelector('.progress-monitor-message');
        this.progressBar = this.container.querySelector('.progress-bar');
        this.logContainer = this.container.querySelector('.log-container');
        this.cancelBtn = this.container.querySelector('#progress-cancel-btn');
        this.closeBtn = this.container.querySelector('#progress-close-btn');

        this.container.querySelector('#progress-minimize-btn').addEventListener('click', () => this.toggleMinimize());
        this.cancelBtn.addEventListener('click', () => this.emit('cancel-job', this.jobId));
        this.closeBtn.addEventListener('click', () => this.hide());

        await super.initialize();
    }

    renderStructure() {
        return `
            <div class="progress-monitor-header">
                <h6 class="progress-monitor-title">AI Task in Progress</h6>
                <div class="progress-monitor-controls">
                    <button class="btn btn-sm btn-link text-secondary" id="progress-minimize-btn" title="Minimize"><i class="bi bi-dash-lg"></i></button>
                    <button class="btn btn-sm btn-link text-danger" id="progress-cancel-btn" title="Cancel Job"><i class="bi bi-stop-circle-fill"></i></button>
                    <button class="btn btn-sm btn-link text-primary" id="progress-close-btn" title="Close" style="display: none;"><i class="bi bi-x-lg"></i></button>
                </div>
            </div>
            <div class="progress-monitor-body">
                <p class="progress-monitor-message">Initializing...</p>
                <div class="progress" style="height: 1.25rem;">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="width: 100%;" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
                <div class="log-container"></div>
            </div>
        `;
    }

    show(jobId, title) {
        this.jobId = jobId;
        this.titleEl.textContent = title;
        this.messageEl.textContent = 'Initializing job...';
        this.progressBar.style.width = '100%'; // Indeterminate start
        this.progressBar.classList.add('progress-bar-animated', 'progress-bar-striped');
        this.progressBar.classList.remove('bg-success', 'bg-danger');
        this.progressBar.textContent = '';
        this.logContainer.innerHTML = '';

        // Reset button visibility for a new job
        this.cancelBtn.style.display = 'inline-block';
        this.closeBtn.style.display = 'none';

        this.container.classList.add('visible');
    }

    update({ progress, message, status, logEntry }) {
        if (message) this.messageEl.textContent = message;

        if (typeof progress === 'number') {
            this.progressBar.style.width = `${progress}%`;
            this.progressBar.textContent = `${progress}%`;
            // If we get a specific progress, stop the indeterminate animation
            if (progress < 100) {
                this.progressBar.classList.remove('progress-bar-animated', 'progress-bar-striped');
            }
        }

        if (logEntry) this.addLog(logEntry);

        if (status === 'completed' || status === 'failed' || status === 'cancelled') {
            this.progressBar.classList.remove('progress-bar-animated');
            this.progressBar.style.width = '100%';

            if (status === 'completed') {
                this.progressBar.classList.add('bg-success');
                this.progressBar.textContent = 'Completed';
            }
            if (status === 'failed' || status === 'cancelled') {
                this.progressBar.classList.add('bg-danger');
                this.progressBar.textContent = status === 'failed' ? 'Failed' : 'Cancelled';
            }

            // Swap the Cancel button for the Close button
            this.cancelBtn.style.display = 'none';
            this.closeBtn.style.display = 'inline-block';

            // REMOVED the automatic hide timeout
        }
    }

    addLog(logEntry) {
        const entryEl = document.createElement('div');
        entryEl.className = 'log-entry';
        entryEl.textContent = `[${new Date().toLocaleTimeString()}] ${logEntry}`;
        this.logContainer.appendChild(entryEl);
        this.logContainer.scrollTop = this.logContainer.scrollHeight;
    }

    toggleMinimize() {
        this.isMinimized = !this.isMinimized;
        this.container.classList.toggle('minimized', this.isMinimized);
        const icon = this.container.querySelector('#progress-minimize-btn i');
        icon.className = this.isMinimized ? 'bi bi-arrows-angle-expand' : 'bi bi-dash-lg';
    }

    hide() {
        this.jobId = null;
        this.container.classList.remove('visible');
    }

    render() { return this.container; }
}